import { useState, useRef, useEffect } from 'react'
import { Plus, Trash2, GripVertical, Move, Pencil, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import type { Resource, ResourceCategory } from '@/types/domain'

interface ResourceCanvasProps {
    category: ResourceCategory
    resources: Resource[]
    onUpdateResource: (resource: Resource) => Promise<void>
    onDeleteResource: (id: string) => Promise<void>
    onAddResource: (name: string) => Promise<void>
}

export function ResourceCanvas({
    category,
    resources,
    onUpdateResource,
    onDeleteResource,
    onAddResource,
}: ResourceCanvasProps) {
    const [newName, setNewName] = useState('')
    const canvasRef = useRef<HTMLDivElement>(null)
    const [isDragging, setIsDragging] = useState<string | null>(null)
    const [isResizing, setIsResizing] = useState<string | null>(null)
    const [interactionStart, setInteractionStart] = useState({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 })
    const [optimisticResources, setOptimisticResources] = useState<Resource[]>(resources)
    const [editingResourceId, setEditingResourceId] = useState<string | null>(null)
    const [editName, setEditName] = useState('')

    const isSavingRef = useRef(false)

    // Sync optimistic resources when props change, but not while interacting or saving
    useEffect(() => {
        if (!isDragging && !isResizing && !isSavingRef.current) {
            setOptimisticResources(resources)
        }
    }, [resources, isDragging, isResizing])

    const handleAdd = () => {
        if (newName.trim()) {
            onAddResource(newName.trim())
            setNewName('')
        }
    }

    const startEditing = (res: Resource) => {
        setEditingResourceId(res.id)
        setEditName(res.name)
    }

    const saveEdit = async () => {
        if (!editingResourceId) return
        const res = optimisticResources.find(r => r.id === editingResourceId)
        if (res && editName.trim() && editName.trim() !== res.name) {
            await onUpdateResource({ ...res, name: editName.trim() })
        }
        setEditingResourceId(null)
    }

    const cancelEdit = () => {
        setEditingResourceId(null)
    }

    // List reordering handlers
    const [draggedItemId, setDraggedItemId] = useState<string | null>(null)

    const handleListDragStart = (e: React.DragEvent, id: string) => {
        setDraggedItemId(id)
        e.dataTransfer.effectAllowed = 'move'
    }

    const handleListDragOver = (e: React.DragEvent, targetId: string) => {
        e.preventDefault()
        if (!draggedItemId || draggedItemId === targetId) return

        const draggedIndex = optimisticResources.findIndex(r => r.id === draggedItemId)
        const targetIndex = optimisticResources.findIndex(r => r.id === targetId)

        if (draggedIndex !== -1 && targetIndex !== -1) {
            const newResources = [...optimisticResources]
            const [draggedItem] = newResources.splice(draggedIndex, 1)
            newResources.splice(targetIndex, 0, draggedItem)
            setOptimisticResources(newResources)
        }
    }

    const handleListDragEnd = async () => {
        if (draggedItemId) {
            const updates = optimisticResources.map((res, index) => ({
                ...res,
                order_index: index + 1
            }))

            for (let i = 0; i < updates.length; i++) {
                const original = resources.find(r => r.id === updates[i].id)
                if (original?.order_index !== updates[i].order_index) {
                    await onUpdateResource(updates[i])
                }
            }
        }
        setDraggedItemId(null)
    }

    // Canvas Interaction Handlers (Move & Resize)
    useEffect(() => {
        if (!isDragging && !isResizing) return

        const getCoords = (e: MouseEvent | TouchEvent) => {
            if ('touches' in e) {
                return { x: e.touches[0].clientX, y: e.touches[0].clientY }
            }
            return { x: e.clientX, y: e.clientY }
        }

        const handleMove = (e: MouseEvent | TouchEvent) => {
            if (!canvasRef.current) return

            const canvasRect = canvasRef.current.getBoundingClientRect()
            const { x, y } = getCoords(e)

            if (isDragging) {
                let newX = x - canvasRect.left - interactionStart.x
                let newY = y - canvasRect.top - interactionStart.y

                // Snap to Grid
                newX = Math.round(newX / 20) * 20
                newY = Math.round(newY / 20) * 20

                // Bounds check
                const res = optimisticResources.find(r => r.id === isDragging)
                if (!res) return

                newX = Math.max(0, Math.min(newX, canvasRect.width - (res.width || 100)))
                newY = Math.max(0, Math.min(newY, canvasRect.height - (res.height || 60)))

                setOptimisticResources(prev =>
                    prev.map(r => r.id === isDragging ? { ...r, x: newX, y: newY } : r)
                )
            } else if (isResizing) {
                let newWidth = interactionStart.width + (x - interactionStart.x)
                let newHeight = interactionStart.height + (y - interactionStart.y)

                // Snap to Grid
                newWidth = Math.round(Math.max(60, newWidth) / 20) * 20
                newHeight = Math.round(Math.max(40, newHeight) / 20) * 20

                // Max Bounds
                const res = optimisticResources.find(r => r.id === isResizing)
                if (!res) return

                newWidth = Math.min(newWidth, canvasRect.width - res.x)
                newHeight = Math.min(newHeight, canvasRect.height - res.y)

                setOptimisticResources(prev =>
                    prev.map(r => r.id === isResizing ? { ...r, width: newWidth, height: newHeight } : r)
                )
            }
        }

        const handleEnd = async () => {
            const activeId = isDragging || isResizing
            if (!activeId) return

            const finalRes = optimisticResources.find(r => r.id === activeId)
            const originalRes = resources.find(r => r.id === activeId)

            setIsDragging(null)
            setIsResizing(null)

            if (finalRes && originalRes && (
                finalRes.x !== originalRes.x ||
                finalRes.y !== originalRes.y ||
                finalRes.width !== originalRes.width ||
                finalRes.height !== originalRes.height
            )) {
                isSavingRef.current = true
                try {
                    await onUpdateResource(finalRes)
                } finally {
                    isSavingRef.current = false
                    // Now sync from server data
                    setOptimisticResources(prev => {
                        // Only update the specific resource from server data
                        const serverRes = resources.find(r => r.id === activeId)
                        if (!serverRes) return prev
                        return prev.map(r => r.id === activeId ? serverRes : r)
                    })
                }
            }
        }

        window.addEventListener('mousemove', handleMove)
        window.addEventListener('mouseup', handleEnd)
        window.addEventListener('touchmove', handleMove, { passive: false })
        window.addEventListener('touchend', handleEnd)
        document.body.classList.add('interaction-active')

        return () => {
            window.removeEventListener('mousemove', handleMove)
            window.removeEventListener('mouseup', handleEnd)
            window.removeEventListener('touchmove', handleMove)
            window.removeEventListener('touchend', handleEnd)
            document.body.classList.remove('interaction-active')
        }
    }, [isDragging, isResizing, interactionStart, optimisticResources, resources, onUpdateResource])

    if (category.visual_type === 'grid') {
        return (
            <Card>
                <CardContent className="p-6">
                    <div className="mb-4 flex gap-2">
                        <Input
                            placeholder="Ressourcen-Name (z.B. Raum 1)"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                        />
                        <Button onClick={handleAdd}>Hinzufügen</Button>
                    </div>
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                        {optimisticResources.map((res) => (
                            <div
                                key={res.id}
                                className="group relative flex flex-col items-center justify-center rounded-lg border border-border bg-card p-4 text-center hover:border-primary/50"
                            >
                                {editingResourceId === res.id ? (
                                    <div className="flex items-center gap-1 w-full px-2">
                                        <Input
                                            value={editName}
                                            onChange={(e) => setEditName(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
                                            autoFocus
                                            className="h-8 py-0 px-2"
                                        />
                                        <Button variant="ghost" size="icon" className="h-7 w-7 text-primary" onClick={saveEdit}>
                                            <Check className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ) : (
                                    <>
                                        <span className="font-medium truncate max-w-full px-2">{res.name}</span>
                                        <div className="absolute right-1 top-1 flex flex-col gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 hover:bg-muted"
                                                onClick={() => startEditing(res)}
                                            >
                                                <Pencil className="h-3 w-3" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 hover:bg-destructive/10 hover:text-destructive"
                                                onClick={() => onDeleteResource(res.id)}
                                            >
                                                <Trash2 className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        )
    }

    if (category.visual_type === 'list') {
        return (
            <Card>
                <CardContent className="p-6">
                    <div className="mb-4 flex gap-2">
                        <Input
                            placeholder="Ressourcen-Name"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                        />
                        <Button onClick={handleAdd}>Hinzufügen</Button>
                    </div>
                    <div className="space-y-2">
                        {optimisticResources.map((res) => (
                            <div
                                key={res.id}
                                draggable={!editingResourceId}
                                onDragStart={(e) => handleListDragStart(e, res.id)}
                                onDragOver={(e) => handleListDragOver(e, res.id)}
                                onDragEnd={handleListDragEnd}
                                className={`flex items-center justify-between rounded-md border border-border p-3 bg-card transition-colors ${draggedItemId === res.id ? 'opacity-50 border-primary' : 'hover:bg-muted/50'}`}
                            >
                                <div className="flex items-center gap-3 flex-1 overflow-hidden mr-2">
                                    <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab active:cursor-grabbing shrink-0" />
                                    {editingResourceId === res.id ? (
                                        <div className="flex items-center gap-2 flex-1">
                                            <Input
                                                value={editName}
                                                onChange={(e) => setEditName(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
                                                onBlur={cancelEdit}
                                                autoFocus
                                                className="h-8"
                                            />
                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-primary" onMouseDown={(e) => e.preventDefault()} onClick={saveEdit}>
                                                <Check className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <span className="font-medium truncate">{res.name}</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className={`h-8 w-8 ${editingResourceId === res.id ? 'text-muted-foreground' : 'text-primary/70 hover:text-primary'}`}
                                        onClick={() => editingResourceId === res.id ? cancelEdit() : startEditing(res)}
                                    >
                                        {editingResourceId === res.id ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-destructive"
                                        onClick={() => onDeleteResource(res.id)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        )
    }

    // Canvas View (Visual Layout Editor)
    return (
        <Card className="overflow-hidden">
            <CardContent className="p-0">
                <style>{`
                    .interaction-active { cursor: grabbing !important; user-select: none !important; }
                    .resizing-active { cursor: nwse-resize !important; }
                `}</style>
                <div className="flex border-b bg-muted/30 p-4 items-center justify-between">
                    <div className="flex gap-2 max-w-xs">
                        <Input
                            placeholder="Name..."
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            className="h-9 bg-background"
                        />
                        <Button size="sm" onClick={handleAdd}>
                            <Plus className="h-4 w-4 mr-1" /> Neu
                        </Button>
                    </div>
                    <p className="text-xs text-muted-foreground italic">
                        Ziehe die Boxen zum Gestalten. Nutze <Pencil className="inline h-3 w-3" /> zum Umbenennen.
                    </p>
                </div>

                <div
                    ref={canvasRef}
                    className="relative h-[500px] bg-slate-50 dark:bg-slate-900/40 overflow-hidden"
                    style={{
                        backgroundImage: 'radial-gradient(circle, #cbd5e1 1px, transparent 1px)',
                        backgroundSize: '20px 20px',
                    }}
                >
                    {optimisticResources.map((res) => (
                        <div
                            key={res.id}
                            className={`absolute select-none rounded border bg-background p-2 shadow-sm transition-shadow hover:shadow-md ${isDragging === res.id || isResizing === res.id ? 'ring-2 ring-primary z-10 scale-[1.02]' : 'hover:border-primary/30'
                                }`}
                            onMouseDown={(e) => {
                                if (editingResourceId === res.id) return
                                setIsDragging(res.id)
                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                                setInteractionStart({
                                    x: e.clientX - rect.left,
                                    y: e.clientY - rect.top,
                                    width: rect.width,
                                    height: rect.height,
                                    left: res.x,
                                    top: res.y
                                })
                            }}
                            onTouchStart={(e) => {
                                if (editingResourceId === res.id) return
                                setIsDragging(res.id)
                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                                const touch = e.touches[0]
                                setInteractionStart({
                                    x: touch.clientX - rect.left,
                                    y: touch.clientY - rect.top,
                                    width: rect.width,
                                    height: rect.height,
                                    left: res.x,
                                    top: res.y
                                })
                            }}
                            style={{
                                left: `${res.x}px`,
                                top: `${res.y}px`,
                                width: `${res.width || 100}px`,
                                height: `${res.height || 60}px`,
                                cursor: isResizing === res.id ? 'nwse-resize' : isDragging === res.id ? 'grabbing' : 'move',
                                touchAction: 'none'
                            }}
                        >
                            {/* Name area — full width at the top */}
                            {editingResourceId === res.id ? (
                                <div className="flex items-center gap-1 w-full" onMouseDown={(e) => e.stopPropagation()}>
                                    <Input
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
                                        autoFocus
                                        className="h-7 py-0 px-2 text-xs flex-1"
                                    />
                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-primary shrink-0" onClick={saveEdit}>
                                        <Check className="h-4 w-4" />
                                    </Button>
                                </div>
                            ) : (
                                <span className="text-sm font-semibold block w-full leading-snug" style={{ wordBreak: 'break-word' }}>{res.name}</span>
                            )}

                            {/* Bottom row: action buttons left, resize hint right */}
                            {!editingResourceId && (
                                <div className="absolute bottom-1 left-1 flex gap-0.5">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-5 w-5 hover:bg-muted text-muted-foreground hover:text-foreground"
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            startEditing(res)
                                        }}
                                    >
                                        <Pencil className="h-3 w-3" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-5 w-5 hover:bg-destructive/10 hover:text-destructive text-muted-foreground"
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            onDeleteResource(res.id)
                                        }}
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </Button>
                                </div>
                            )}

                            {/* Resize Handle */}
                            {!editingResourceId && (
                                <div
                                    className="absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize flex items-end justify-end p-0.5 group/resize"
                                    onMouseDown={(e) => {
                                        e.stopPropagation()
                                        setIsResizing(res.id)
                                        setInteractionStart({
                                            x: e.clientX,
                                            y: e.clientY,
                                            width: res.width || 100,
                                            height: res.height || 60,
                                            left: res.x,
                                            top: res.y
                                        })
                                    }}
                                    onTouchStart={(e) => {
                                        e.stopPropagation()
                                        setIsResizing(res.id)
                                        const touch = e.touches[0]
                                        setInteractionStart({
                                            x: touch.clientX,
                                            y: touch.clientY,
                                            width: res.width || 100,
                                            height: res.height || 60,
                                            left: res.x,
                                            top: res.y
                                        })
                                    }}
                                >
                                    <div className="w-2 h-2 border-r-2 border-b-2 border-muted-foreground/30 group-hover/resize:border-primary transition-colors" />
                                </div>
                            )}

                            {!editingResourceId && (
                                <div className="mt-1 flex items-center justify-center opacity-30 pointer-events-none">
                                    <Move className="h-4 w-4" />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    )
}
